import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import axiosInstance from '../utils/axiosInstance';
import { plotlyStyle } from '../utils/plotlyStyle';
import { Stack, Button, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { MESSAGES } from '../constants/messages';

const ChartRevenue = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const now = dayjs();

  const defaultFrom = now
    .subtract(11, 'month')
    .startOf('month');

  const defaultTo = now.endOf('month');

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const fetchData = async (params = {}) => {
    setLoading(true);

    try {
      const res = await axiosInstance.get(
        '/chart/revenue-order-by-month',
        { params }
      );

      setData(res.data || []);
      setError(null);
    } catch (err) {
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const setThisMonth = () => {
    const f = dayjs().startOf('month');
    const t = dayjs().endOf('month');

    setFrom(f);
    setTo(t);

    fetchData({
      from: f.format('YYYY-MM-DD'),
      to: t.format('YYYY-MM-DD'),
    });
  };

  const setLast3Months = () => {
    const f = dayjs().subtract(2, 'month').startOf('month');
    const t = dayjs().endOf('month');

    setFrom(f);
    setTo(t);

    fetchData({
      from: f.format('YYYY-MM-DD'),
      to: t.format('YYYY-MM-DD'),
    });
  };

  const setLast12Months = () => {
    const f = dayjs()
      .subtract(11, 'month')
      .startOf('month');

    const t = dayjs().endOf('month');

    setFrom(f);
    setTo(t);

    fetchData({
      from: f.format('YYYY-MM-DD'),
      to: t.format('YYYY-MM-DD'),
    });
  };

  if (loading) {
    return <Typography>{MESSAGES.LOADING}</Typography>;
  }

  if (error) {
    return <Typography>{error || MESSAGES.LOAD_FAILED}</Typography>;
  }

  const months = data.map(item =>
    dayjs(item.month).format('YYYY-MM-01')
  );

  const plotData = [
    {
      x: months,
      y: data.map(item => Number(item.revenue || 0)),
      type: 'scatter',
      mode: 'lines+markers',
      name: '売上高',
      line: {
        color: plotlyStyle.colors.primary,
      },
      hovertemplate:
        '%{x}<br>売上高: %{y:,.0f}円<extra></extra>',
    },
    {
      x: months,
      y: data.map(item => Number(item.orders || 0)),
      type: 'scatter',
      mode: 'lines+markers',
      name: '受注高',
      line: {
        color: plotlyStyle.colors.secondary,
      },
      hovertemplate:
        '%{x}<br>受注高: %{y:,.0f}円<extra></extra>',
    },
  ];

  const layout = {
    ...plotlyStyle.layout,

    title: {
      text:
        from && to
          ? `売上高・受注高 推移（${from.format('YYYY-MM')}〜${to.format('YYYY-MM')}）`
          : '直近12ヶ月の売上高・受注高推移',
    },

    xaxis: {
      ...plotlyStyle.layout.xaxis,
      title: '年月',
      type: 'date',
      tickformat: '%Y年%m月',
      dtick: 'M1',
      ticklabelmode: 'period',
    },

    yaxis: {
      ...plotlyStyle.layout.yaxis,
      title: '金額 (円)',
      tickformat: ',d',
      separatethousands: true,
      exponentformat: 'none',
      showexponent: 'none',
      automargin: true,
    },

    legend: {
      orientation: 'h',
      y: -0.25,
    },

    margin: {
      l: 80,
      r: 30,
      t: 70,
      b: 80,
    },
  };

  return (
    <>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="ja"
      >
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <DatePicker
              label="開始月"
              views={['year', 'month']}
              value={from ?? defaultFrom}
              onChange={(value) => setFrom(value)}
              maxDate={to}
              format="YYYY年M月"
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />

            <Typography>〜</Typography>

            <DatePicker
              label="終了月"
              views={['year', 'month']}
              value={to ?? defaultTo}
              onChange={(value) => setTo(value)}
              minDate={from}
              format="YYYY年M月"
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />

            <Button
              size="small"
              variant="contained"
              disabled={!from || !to}
              onClick={() =>
                fetchData({
                  from: from
                    .startOf('month')
                    .format('YYYY-MM-DD'),
                  to: to
                    .endOf('month')
                    .format('YYYY-MM-DD'),
                })
              }
            >
              適用
            </Button>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={setThisMonth}
            >
              今月
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={setLast3Months}
            >
              直近3ヶ月
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={setLast12Months}
            >
              直近12ヶ月
            </Button>
          </Stack>
        </Stack>
      </LocalizationProvider>

      <div>
        <Plot
          data={plotData}
          layout={layout}
          config={{
            responsive: true,
            displayModeBar: false,
          }}
          style={{
            width: '100%',
            height: '100%',
            minHeight: 500,
          }}
          useResizeHandler={true}
        />
      </div>
    </>
  );
};

export default ChartRevenue;